import type { AccessibilitySettings } from '../shared/contracts'

export type ArcadeSound = 'dive' | 'lock' | 'shift' | 'win' | 'miss' | 'raid'

export class ArcadeAudio {
  private context?: AudioContext
  private master?: GainNode
  private music?: GainNode
  private sfx?: GainNode
  private ambience?: OscillatorNode
  private settings?: AccessibilitySettings
  private resuming = false
  private resumeCallbacks: (() => void)[] = []

  unlock(settings: AccessibilitySettings): void {
    this.settings = settings
    if (!this.context) this.createGraph()
    if (this.context?.state === 'suspended') this.resume()
    this.apply(settings)
  }

  apply(settings: AccessibilitySettings): void {
    this.settings = settings
    if (!this.context || !this.master || !this.music || !this.sfx) return
    const now = this.context.currentTime
    this.master.gain.setTargetAtTime(settings.masterVolume, now, 0.03)
    this.music.gain.setTargetAtTime(settings.musicVolume * 0.055, now, 0.03)
    this.sfx.gain.setTargetAtTime(settings.sfxVolume * 0.22, now, 0.03)
  }

  play(sound: ArcadeSound): void {
    if (!this.context && this.settings) this.unlock(this.settings)
    if (!this.context || !this.sfx) return
    if (this.context.state !== 'running') {
      this.resume(() => this.play(sound))
      return
    }
    const patterns: Record<ArcadeSound, Array<[number, number, OscillatorType]>> = {
      dive: [[190, 0, 'sine'], [285, 0.09, 'triangle']],
      lock: [[440, 0, 'triangle'], [660, 0.07, 'sine']],
      shift: [[330, 0, 'sawtooth'], [520, 0.06, 'triangle']],
      win: [[420, 0, 'triangle'], [630, 0.09, 'triangle'], [840, 0.18, 'sine'], [1_050, 0.28, 'sine']],
      miss: [[300, 0, 'triangle'], [210, 0.1, 'sine']],
      raid: [[110, 0, 'sawtooth'], [165, 0.12, 'triangle'], [220, 0.24, 'sine']],
    }
    for (const [frequency, delay, type] of patterns[sound]) this.tone(frequency, delay, type, sound === 'win' ? 0.22 : 0.15)
  }

  private createGraph(): void {
    const AudioContextClass = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioContextClass) return
    this.context = new AudioContextClass()
    this.master = this.context.createGain()
    this.music = this.context.createGain()
    this.sfx = this.context.createGain()
    this.music.connect(this.master)
    this.sfx.connect(this.master)
    this.master.connect(this.context.destination)

    this.ambience = this.context.createOscillator()
    this.ambience.type = 'sine'
    this.ambience.frequency.value = 55
    this.ambience.connect(this.music)
    this.ambience.start()
  }

  private resume(afterResume?: () => void): void {
    if (afterResume) this.resumeCallbacks.push(afterResume)
    if (!this.context || this.resuming) return
    this.resuming = true
    void this.context.resume()
      .then(() => {
        this.resuming = false
        if (this.context?.state === 'running') {
          const callbacks = this.resumeCallbacks.splice(0)
          for (const callback of callbacks) callback()
        }
      })
      .catch(() => {
        this.resuming = false
        this.resumeCallbacks = []
      })
  }

  private tone(frequency: number, delay: number, type: OscillatorType, duration: number): void {
    if (!this.context || !this.sfx) return
    const startsAt = this.context.currentTime + delay
    const oscillator = this.context.createOscillator()
    const envelope = this.context.createGain()
    oscillator.type = type
    oscillator.frequency.setValueAtTime(frequency, startsAt)
    envelope.gain.setValueAtTime(0.0001, startsAt)
    envelope.gain.exponentialRampToValueAtTime(0.7, startsAt + 0.018)
    envelope.gain.exponentialRampToValueAtTime(0.0001, startsAt + duration)
    oscillator.connect(envelope)
    envelope.connect(this.sfx)
    oscillator.start(startsAt)
    oscillator.stop(startsAt + duration + 0.03)
  }
}
